import React, { createContext, useContext, useEffect, useState } from "react";
import globalConstants from "../utils/constants";

interface ApiConfig {
  baseUrl: string;
  authToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

interface ApiConfigContextType extends ApiConfig {
  updateAuthToken: (token: string) => void;
  clearAuth: () => void;
}

const ApiConfigContext = createContext<ApiConfigContextType | undefined>(
  undefined
);

export const ApiConfigProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [config, setConfig] = useState<ApiConfig>({
    baseUrl: process.env.REACT_APP_BASE_URL || "",
    authToken: null,
    isAuthenticated: false,
    isLoading: true,
  });

  useEffect(() => {
    const storedToken = localStorage.getItem(globalConstants.AUTH_TOKEN);
    if (storedToken) {
      setConfig((prev) => ({
        ...prev,
        authToken: storedToken,
        isAuthenticated: true,
        isLoading: false,
      }));
    } else {
      setConfig((prev) => ({
        ...prev,
        isLoading: false,
      }));
    }
  }, []);

  const updateAuthToken = (token: string) => {
    setConfig((prev) => ({
      ...prev,
      authToken: token,
      isAuthenticated: true,
      isLoading: false,
    }));
    localStorage.setItem(globalConstants.AUTH_TOKEN, token);
  };

  const clearAuth = () => {
    setConfig((prev) => ({
      ...prev,
      authToken: null,
      isAuthenticated: false,
      isLoading: false,
    }));
    localStorage.removeItem(globalConstants.AUTH_TOKEN);
  };

  const contextValue: ApiConfigContextType = {
    ...config,
    updateAuthToken,
    clearAuth,
  };

  return (
    <ApiConfigContext.Provider value={contextValue}>
      {children}
    </ApiConfigContext.Provider>
  );
};

export const useApiConfig = (): ApiConfigContextType => {
  const context = useContext(ApiConfigContext);
  if (context === undefined) {
    throw new Error("useApiConfig must be used within an ApiConfigProvider");
  }
  return context;
};

export const useApiProps = () => {
  const { baseUrl, authToken, isLoading } = useApiConfig();

  if (isLoading) {
    return null;
  }

  if (!authToken) {
    throw new Error(
      "Auth token is not available. User might not be authenticated."
    );
  }

  return {
    baseUrl,
    authToken,
  };
};
